import { COLORS } from '../constants/colors';

export interface EventDateInfo {
  dateObj: Date;
  month: string;
  day: number;
  dayOfWeek: string;
  isSunday: boolean;
  isSaturday: boolean;
  fullMonthYear: string;
}

/**
 * Get formatted date information for an event
 * @param dateString - Date string in ISO format
 * @returns Event date information
 */
export const getEventDateInfo = (dateString: string): EventDateInfo => {
  const eventDateObj = new Date(dateString);
  const dayOfWeek = eventDateObj.getDay();
  return {
    dateObj: eventDateObj,
    month: eventDateObj.toLocaleDateString('zh-TW', { month: 'short' }),
    day: eventDateObj.getDate(),
    dayOfWeek: eventDateObj.toLocaleDateString('zh-TW', { weekday: 'short' }),
    isSunday: dayOfWeek === 0,
    isSaturday: dayOfWeek === 6,
    fullMonthYear: eventDateObj.toLocaleDateString('zh-TW', { year: 'numeric', month: 'long' })
  };
};

/**
 * Get color for date based on day of week
 * @param isSunday - Whether the date is Sunday
 * @param isSaturday - Whether the date is Saturday
 * @returns Color hex code
 */
export const getDateColor = (isSunday: boolean, isSaturday: boolean = false): string => {
  if (isSunday) return COLORS.DATE.SUNDAY;
  if (isSaturday) return COLORS.DATE.SATURDAY;
  return COLORS.DATE.DEFAULT;
};

/**
 * Check if month header should be shown
 * @param currentMonth - Current month string
 * @param previousMonth - Previous month string or null
 * @param isFirstEvent - Whether this is the first event
 * @returns True if header should be shown
 */
export const shouldShowMonthHeader = (
  currentMonth: string,
  previousMonth: string | null,
  isFirstEvent: boolean
): boolean => {
  return isFirstEvent || currentMonth !== previousMonth;
};

/**
 * Check if this is the last event in a month
 * @param currentMonth - Current month string
 * @param nextMonth - Next month string or null
 * @param isLastEvent - Whether this is the last event overall
 * @returns True if this is the last event in the month
 */
export const isLastEventInMonth = (
  currentMonth: string,
  nextMonth: string | null,
  isLastEvent: boolean
): boolean => {
  return isLastEvent || currentMonth !== nextMonth;
};

/**
 * Get today's date in YYYY-MM-DD format
 * @returns Today's date string
 */
export const getTodayString = (): string => {
  return new Date().toISOString().split('T')[0];
};

/**
 * Check if a date is in the past
 * @param dateString - Date string to check
 * @returns True if date is in the past
 */
export const isPastDate = (dateString: string): boolean => {
  return dateString < getTodayString();
};

/**
 * Check if a date is in the future
 * @param dateString - Date string to check
 * @returns True if date is in the future
 */
export const isFutureDate = (dateString: string): boolean => {
  return dateString >= getTodayString();
};
